import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { SEO } from "@/components/SEO";
import { supabase } from "@/integrations/supabase/client";
import { BlogCard } from "@/components/blog/BlogCard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Breadcrumbs from "@/components/ui/Breadcrumbs";
import blogHeroBg from "@/assets/blog-hero-bg.jpg";

const categories = [
  "All",
  "Pressure Washing Tips",
  "Window Cleaning",
  "Carpet Care",
  "Gutter Maintenance",
  "Solar Panel Care",
  "Soft Wash Services",
  "Maintenance Guides",
  "Local News",
  "Industry Insights",
];

const BlogPage = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");

  const { data: posts, isLoading } = useQuery({
    queryKey: ["blog-posts", selectedCategory],
    queryFn: async () => {
      let query = supabase
        .from("blog_posts")
        .select("*")
        .eq("is_published", true)
        .order("published_date", { ascending: false });

      if (selectedCategory !== "All") {
        query = query.eq("category", selectedCategory);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
  });

  const filteredPosts = posts?.filter((post) =>
    post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    post.excerpt.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <>
      <SEO
        title="Cleaning Tips & News Blog | Ventura Power Washing"
        description="Expert cleaning tips, maintenance guides, and industry insights for pressure washing, window cleaning, and more from Ventura Power Washing."
        keywords="pressure washing tips, cleaning guides, ventura cleaning blog, maintenance advice, power washing news"
        path="/blog"
      />

      <Navbar />

      {/* Hero Banner */}
      <div
        className="relative bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${blogHeroBg})` }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/65 via-black/55 to-black/70"></div>
        <div className="relative z-10 container mx-auto px-4 pt-24 pb-16 text-center text-white">
          <h1 className="text-5xl md:text-6xl font-bold mb-4">
            Our Blog
          </h1>
          <p className="text-xl md:text-2xl opacity-90 max-w-2xl mx-auto">
            Expert cleaning tips, maintenance guides, and industry insights
            from Ventura County's trusted cleaning professionals
          </p>
        </div>
      </div>

      <main className="min-h-screen bg-gradient-to-b from-background to-muted/20">
        <section className="container mx-auto px-4 py-12">
          {/* Search Bar */}
          <div className="max-w-md mx-auto mb-8">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap gap-2.5 justify-center mb-12">
            {categories.map((category) => (
              <Button
                key={category}
                variant={selectedCategory === category ? "default" : "outline"}
                onClick={() => setSelectedCategory(category)}
                size="sm"
                className={
                  selectedCategory === category
                    ? "bg-ventura-blue hover:bg-ventura-darkBlue text-white rounded-full px-5"
                    : "rounded-full px-5 border-border text-muted-foreground hover:text-foreground hover:border-ventura-blue"
                }
              >
                {category}
              </Button>
            ))}
          </div>

          {/* Blog Posts Grid */}
          {isLoading ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">Loading articles...</p>
            </div>
          ) : filteredPosts && filteredPosts.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map((post) => (
                <BlogCard
                  key={post.id}
                  id={post.id}
                  title={post.title}
                  slug={post.slug}
                  excerpt={post.excerpt}
                  featuredImage={post.featured_image || undefined}
                  publishedDate={post.published_date}
                  category={post.category}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-muted-foreground">
                No articles found. Try adjusting your search or filter.
              </p>
            </div>
          )}
        </section>
      </main>

      <Footer />
    </>
  );
};

export default BlogPage;
